/**
 * THE REACT KEY FOR A CONSOLE PAGE, AND WHY IT HAS TO EXIST.
 *
 * Switching marketplace is a full navigation, so the server resolves the new
 * tenant's context and membership from scratch and the props are always the
 * right ones. React, however, does not remount a component merely because a
 * route PARAMETER changed: `/admin/community/A` and `/admin/community/B` are the
 * same `[communityId]` page, so the instance and every `useState` in it survive
 * the switch. A state initialiser runs once, on mount, and never again.
 *
 * That silently carried one marketplace's client state into another's console:
 *
 *   - `settings.tsx` seeds four forms from props, so marketplace A's name,
 *     country, storefront status and listing policy stayed on screen under B's
 *     header — and submitting would have written A's values into B.
 *   - `telegram.tsx` seeds its ingestion toggles from A's binding and holds a
 *     live `issued` connect code, so B's owner could be shown A's code and
 *     invited to bind it into B's group.
 *   - `categories.tsx` (`editing`), `rules.tsx` (`draft`) and the
 *     `listings.tsx` / `moderation.tsx` confirmation dialogs (`pending`) all
 *     hold an ENTITY ID belonging to the marketplace just left, so a confirm
 *     would post a foreign id at the new tenant's endpoint.
 *
 * Keying the page on the community id makes a tenant change a remount, which
 * discards all of it. The server was never the weak point here; the client's
 * memory was. `undefined` when the page is not a console page, so ordinary
 * navigation keeps React's default reconciliation.
 *
 * THIS MODULE DELIBERATELY IMPORTS NOTHING.
 *
 * Its one caller is `_app.tsx`, which is the root of the client bundle: every
 * page on the site, including the public storefront, pays for whatever it pulls
 * in. Living in `@/lib/community/console` meant importing the console capability
 * map and, through it, `@/lib/community/authorization` and its role tables, into
 * every one of those bundles to compute a single string. A leaf with no imports
 * cannot drag anything along and cannot take part in an import cycle.
 *
 * It deliberately does NOT include the section: moving between sections of one
 * marketplace already changes the pathname, so it remounts anyway, and keying on
 * it would throw away scroll position for no benefit.
 */
export const consolePageKey = (communityId: string | null | undefined): string | undefined =>
    communityId ? `console:${communityId}` : undefined
